import { PageWithMenu, ProfileDataBox, UserInfoBox } from '../../../lib/components';
import { useTranslation } from 'react-i18next';
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
  useLocation
} from 'react-router';
import { useStore } from '../../../hooks/useStore';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { profileSubmenuConfig } from '../../../submenus';
import { DatePickerBox } from '../../../lib/components/compound/pages/pageWithMenu/datePickerBox';
import { format } from 'date-fns';
import Util from '../../../misc/util';
import { ReceiptPageContext } from '../../receipts/detailView';
import { ReceiptStatusEnum } from '@pxtra-client-lib-legacy/gen';
import { Button } from '../../../lib/components/atomic/button';
import { useGetEmployee } from '../../../queries/employee';
import { checkHasCompanyBenefitSets } from '../../../lib/util/pricingModel';
import { useQueryClient } from '@tanstack/react-query';
import { usePaymentData } from '../../../queries/company';

interface Context {
  date: [Date, Dispatch<SetStateAction<Date | undefined>>];
  status: [ReceiptStatusEnum, Dispatch<SetStateAction<ReceiptStatusEnum | undefined>>];
  context: ReceiptPageContext;
}

export const useDate = () => {
  return useOutletContext<Context>().date;
};

export const useStatus = () => {
  return useOutletContext<Context>().status;
};

export const usePageContext = () => {
  return useOutletContext<Context>().context;
};

const initDate = (searchParams: URLSearchParams) => {
  const date = searchParams.get('date');
  return date ? new Date(date) : new Date();
};

export const ProfilPagesPartial = observer(() => {
  const { employeeStore, receiptStore, companyStore } = useStore();
  const { employeeId, receiptId } = useParams();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState<Date | undefined>(initDate(searchParams));
  const [disableFuture, setDisableFuture] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const getEmployeeId = () => {
    return employeeId ? employeeId : employeeStore.currentEmployee?.id;
  };

  const { data: extendedEmployeeData } = useGetEmployee(
    employeeId ?? employeeStore.currentEmployee?.id ?? ''
  );

  const { data: paymentData } = usePaymentData(companyStore.company?.id);
  const invitationDisabled =
    checkHasCompanyBenefitSets(paymentData?.pricing_model, paymentData?.booked_benefits) &&
    extendedEmployeeData?.next_month_benefit_set == null;

  useEffect(() => {
    if (
      location.pathname.endsWith('/benefits/budget') ||
      location.pathname.endsWith('/benefits/external') ||
      location.pathname.endsWith('/benefits/overview')
    ) {
      setDisableFuture(false);
    } else {
      setDisableFuture(true);
    }
  }, [location]);

  useEffect(() => {
    let isCanceled = false;
    const fetchUser = async () => {
      if (!isCanceled && employeeId) {
        employeeStore.fetchOne(employeeId);
      }
    };
    fetchUser();
    return () => {
      isCanceled = true;
    };
  }, [employeeId]);

  useEffect(() => {
    let isCanceled = false;
    const fetchData = async () => {
      if (!isCanceled && receiptId) {
        await receiptStore.fetchOne(receiptId);
        if (receiptStore.current?.employee) {
          employeeStore.fetchOne(receiptStore.current.employee);
        }
      }
    };

    fetchData();
    return () => {
      isCanceled = true;
    };
  }, [receiptId]);

  useEffect(() => {
    if (date) setSearchParams(`date=${format(date, 'yyyy-MM')}`, { replace: true });
  }, [date]);

  useEffect(() => {
    if (!companyStore.company || !extendedEmployeeData) return;
    if (extendedEmployeeData.company === companyStore.company.id) return;

    if (extendedEmployeeData.company) {
      companyStore.setCurrentCompanyById(extendedEmployeeData.company).then(() => {
        queryClient.removeQueries({ queryKey: ['employees'] });
      });
    }
  }, [companyStore, extendedEmployeeData, employeeStore.currentEmployee, queryClient]);

  return (
    <PageWithMenu
      menuConfig={profileSubmenuConfig(
        t,
        getEmployeeId(),
        date,
        employeeStore.receiptBenefits.length == 0
      )}
      rightColumn={
        <>
          {!window.location.pathname.match(/\/employees\/(me|[0-9a-f-]+?)\/data/) && (
            <DatePickerBox
              date={date}
              onChange={setDate}
              minDate={Util.getMinDate(15)}
              maxDate={Util.getMaxDate(4)}
              disableFuture={disableFuture}
              openTo="month"
            />
          )}
          <ProfileDataBox
            {...{
              employeeId: employeeId ?? '',
              address: extendedEmployeeData?.address,
              user: extendedEmployeeData?.user,
              status: extendedEmployeeData?.status,
              lastName: extendedEmployeeData?.general_data?.last_name,
              invitationDisabled
            }}
          />
        </>
      }
      elementAboveMenu={
        <UserInfoBox
          employeeFirstName={extendedEmployeeData?.general_data?.first_name || ''}
          employeeLastName={extendedEmployeeData?.general_data?.last_name || ''}
          employeePosition={extendedEmployeeData?.position_data?.position_name || ''}
          employeeStartDate={extendedEmployeeData?.position_data?.entry_date || ''}
          profilePictureUrl={extendedEmployeeData?.general_data?.profile_picture?.url}
          onAvatarClicked={() => navigate(`/employees/${employeeId}/data/picture`)}
        />
      }
      elementBelowMenu={
        <Button variant="secondary" onClick={() => navigate('/employees')}>
          {t('employee:TO_OVERVIEW')}
        </Button>
      }
    >
      <Outlet context={{ date: [date, setDate], context: 'employee' }} />
    </PageWithMenu>
  );
});
